import React from 'react'

import {
  Button,
  Modal,
  ModalInjectedProps,
  Avatar,
  WorkspaceLogo,
} from '@sketch/components'
import {
  GetUserSettingsDocument,
  GetWorkspacesDocument,
  useAcceptWorkspaceInviteMutation,
  UserWorkspaceMembershipFragment,
} from '@sketch/gql-types'

import { useToast } from '@sketch/toasts'
import { useUserProfile } from '@sketch/modules-common'

import {
  ArrowIconWrapper,
  Header,
  InlineWorkspaceLogo,
  ModalBody,
} from './AcceptWorkspaceInviteModal.styles'

import { ReactComponent as ArrowsRightLeftIcon } from '@sketch/icons/img-arrows-right-left-17'

interface AcceptWorkspaceInviteModalProps extends ModalInjectedProps {
  membership: UserWorkspaceMembershipFragment
}

export const AcceptWorkspaceInviteModal = (
  props: AcceptWorkspaceInviteModalProps
) => {
  const { hideModal, membership } = props

  const { workspace } = membership
  const { showToast } = useToast()

  const { data } = useUserProfile()
  const user = data?.me

  const [acceptMembership, { loading }] = useAcceptWorkspaceInviteMutation({
    variables: { membershipId: membership.identifier },
    onError: 'show-toast',
    onCompleted: ({ acceptWorkspaceInvite }) => {
      showToast(
        `Joined ${acceptWorkspaceInvite.membership.workspace.name} successfully`,
        'positive'
      )
      hideModal()
    },
    refetchQueries: () => [
      { query: GetUserSettingsDocument },
      { query: GetWorkspacesDocument },
    ],
    awaitRefetchQueries: true,
  })

  return (
    <Modal onCancel={hideModal}>
      <Header url={workspace.avatar?.large || ''}>
        {user && (
          <Avatar
            size="64px"
            src={user.avatar?.large || undefined}
            name={user.name || '' /** Name should always be there */}
          />
        )}

        <ArrowIconWrapper>
          <ArrowsRightLeftIcon />
        </ArrowIconWrapper>

        <WorkspaceLogo
          workspaceName={workspace.name}
          src={workspace.avatar?.large}
          size="64px"
        />
      </Header>
      <Modal.Header>Workspace Invite</Modal.Header>
      <ModalBody>
        You’re invited to join{' '}
        <InlineWorkspaceLogo size="17px" src={workspace.avatar?.small} />{' '}
        <b>{workspace.name}</b> as{' '}
        {membership.isEditor ? 'an Editor' : 'a Viewer'}, using your{' '}
        <b>{membership.user?.email}</b> account.
      </ModalBody>
      <Modal.Footer>
        <Button variant="secondary" onClick={hideModal}>
          Cancel
        </Button>

        <Button
          variant="primary"
          onClick={() => acceptMembership()}
          loading={loading}
        >
          Accept Invite
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
